import { withErrorBoundary } from "@sentry/nextjs";
import cn from "classnames";
import { gql } from "graphql-request";
import { VFC } from "react";
import styled from "styled-components";
import { LegacyImage, SupTitle } from "components";
import { useAnchor } from "hooks";
import { Features7BContent } from "types/graphql/generated";

interface Props {
  className?: string;
  content: Features7BContent;
}

const Features7b: VFC<Props> = withErrorBoundary(
  ({ content: { features7bSupTitle, features7bTitle, features7bCard }, className = "" }) => {
    useAnchor("features7b");

    return (
      <StyledFeatures className={cn("features7b Content", className)}>
        <div className="Features7b__titles">
          <SupTitle className="Features7b__supTitle">{features7bSupTitle}</SupTitle>
          <h2 className="Features7b__title Font_48_54_600 lg:Font_40_48_600">{features7bTitle}</h2>
        </div>
        <ul className="Features7b__cards">
          {features7bCard.map(
            ({
              features7bCardTitle,
              features7bCardIcon: {
                data: { features7bCardIcon },
              },
            }) => (
              <li key={features7bCardTitle} className="Features7b__card">
                <LegacyImage
                  {...features7bCardIcon}
                  layout="fill"
                  objectFit="contain"
                  transform={{ format: "svg" }}
                  className="Features7b__cardIcon"
                />
                <h3 className="Features7b__cardTitle Font_24_32_600 sm:Font_18_26_600">{features7bCardTitle}</h3>
              </li>
            )
          )}
        </ul>
      </StyledFeatures>
    );
  },
  {}
);

const StyledFeatures = styled.section`
  display: flex;
  justify-content: space-between;
  margin-top: -64px;
  padding-top: 64px;

  .Features7b__titles {
    width: 522px;
  }

  .Features7b__title {
    margin-top: 32px;
  }

  .Features7b__cards {
    width: 604px;
    padding: 16px 0 0 32px;
  }

  .Features7b__card {
    display: flex;
    align-items: center;
  }

  .Features7b__card:not(:first-child) {
    margin-top: 40px;
  }

  .Features7b__cardIcon {
    position: relative;
    width: 64px;
    height: 64px;
    flex-shrink: 0;
  }

  .Features7b__cardTitle {
    margin-left: 32px;
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.xl}px`}) {
    .Features7b__titles {
      width: 375px;
    }

    .Features7b__supTitle {
      display: none;
    }

    .Features7b__title {
      margin-top: 0;
    }

    .Features7b__cards {
      width: 473px;
      margin-left: 32px;
      padding: 16px 0 0;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.lg}px`}) {
    flex-direction: column;

    .Features7b__titles {
      width: 426px;
    }

    .Features7b__cards {
      width: 100%;
      margin-top: 40px;
      margin-left: 0;
    }

    .Features7b__card:not(:first-child) {
      margin-top: 32px;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.md}px`}) {
    .Features7b__cards {
      margin-top: 48px;
      padding: 0;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.sm}px`}) {
    .Features7b__titles {
      width: 100%;
    }

    .Features7b__card {
      min-height: 72px;
    }

    .Features7b__card:not(:first-child) {
      margin-top: 32px;
    }

    .Features7b__cardTitle {
      margin-left: 24px;
    }
  }
`;

gql`
  fragment Features7BContent on ComponentFeaturesFeatures7B {
    features7bSupTitle
    features7bTitle
    features7bCard {
      features7bCardTitle
      features7bCardIcon {
        data {
          features7bCardIcon: attributes {
            src: hash
            alt: alternativeText
            ext
          }
        }
      }
    }
  }
`;

export { Features7b };
export type { Props as Features7bProps };
